import { Component, computed, effect, input, ElementRef, inject, Input, OnInit, signal, viewChild } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ApplicationType, IBaseCard, IBaseT1Card, IColumnData, isiFirmTaxDashboard, type IT1Tags, TranslateObject } from "@app/views-interfaces";
import { CardDisplayService } from "@shared/card-display";
import { CardViewService } from "@shared/card-view-service";
import {
    AnalyticsService,
    DashboardsService,
    FilterSortModalHandlerService,
    HelpersService,
    IFirmContactInformationService,
} from "@shared/index";
import { PendingChangesPopupComponent } from "@shared/pending-changes-popup";
import { format, fromUnixTime } from "date-fns";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { take } from "rxjs";
import type { UserDataModel } from "@app/settings-interfaces";
import { JumpstartComponentsModule } from "@wk/components-v3-angular17";
import { WkTranslatePipe } from "../../shared/utils/wk-translate-pipe/wk-translate.pipe";
import { CardTitlePipe } from "../../shared/card-pipe/card-title.pipe";
import { UserInitialPipe } from "./UserInitialPipe/user-initial.pipe";
import { UserNamePipe } from "./UserNamePipe/user-name.pipe";
import { ContactsAccessType } from "@app/shared/interfaces/contacts-access-type.enum";
import { LanguageService } from "@app/shared/services/language.service";

@Component({
    selector: "wk-card-t1",
    templateUrl: "./card-t1.component.html",
    styleUrl: "./card-t1.component.scss",
    host: {
        class: "wk-card-t1",
        "[id]": "divId",
        "(click)": "openCard($event)",
        "(mouseenter)": "showTooltipOnPartnerManager($event, _cardData)",
        "(mouseleave)": "onMouseLeave()",
        "[class.wk-card-t1__grid]": "format === 'grid'",
        "[class.wk-card-t1__column]": "format === 'column'",
        "[class.row]": "format === 'grid'",
        "[class.wk-card-t1__column--quiet]": "format === 'column' && quietDaysFromNow > 0",
        "[class.wk-card-t1__column--danger]": "this.cardViewService.currentViewType !== 'completed' && format === 'column' && (isStepAlert)",
        "[class.wk-card-t1__column--warning]":
            "this.cardViewService.currentViewType !== 'completed' && format === 'column' && (isStepWarning && !isStepAlert)",
        "[class.no-drag]": "format === 'column' && dragCard",
        "[class.notStarted]": "format === 'grid' && cardViewService.currentViewType === 'notStarted'",
        "[class.completed]": "format === 'grid' && cardViewService.currentViewType === 'completed'",
    },
    imports: [JumpstartComponentsModule, WkTranslatePipe, CardTitlePipe, TranslatePipe, UserInitialPipe, UserNamePipe],
})
export class CardT1Component implements OnInit {
    private readonly helpersService = inject(HelpersService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly translateService = inject(TranslateService);
    public readonly cardViewService = inject(CardViewService);
    private readonly router = inject(Router);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly languageService = inject(LanguageService);
    private readonly modalService = inject(BsModalService);
    private readonly modalCoordinationService = inject(FilterSortModalHandlerService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly iFirmContactInformationService = inject(IFirmContactInformationService);

    private hasPendingFilterChanges = false;
    bsModalRef?: BsModalRef;

    public columnData = input<IColumnData<IBaseCard>>();
    public matchedField = input<string>("");

    _cardData: IBaseT1Card;
    private cardData$ = signal<IBaseT1Card>(null);
    private _statusIds!: string[];

    protected assignedUser = computed<UserDataModel | null>(() => {
        const cardData = this.cardData$();
        const columnData = this.columnData();
        if (!cardData || !columnData) return null;

        return this.helpersService.getAssignedUserForCardT1(cardData, columnData);
    });

    @Input() set cardData(value: IBaseT1Card) {
        this._cardData = value;
        this.cardData$.set(value);

        if (value && !this._divIdSet) {
            this.divId = this.generateDivId(value.cardId);
            this._divIdSet = true;
        }

        if (this._cardData && this.statusIds) {
            this.calcAllFieldLabels();
            this.setTagNames();
        }
    }
    @Input() format: string;
    @Input() set statusIds(value: string[]) {
        this._statusIds = value;

        if (this._cardData && this._statusIds) {
            this.calcAllFieldLabels();
            this.setTagNames();
        }
    }

    get statusIds(): string[] {
        return this._statusIds;
    }
    @Input() hideArrows: boolean;
    @Input() columnId: string;
    deadlineDate = null;
    deadlineDaysFromNow = null;
    deadlineDateFromNow = null;
    complexity = new TranslateObject();
    clientFileCount = 1;
    daysSinceReady = 0;
    daysSinceInStep = 0;
    isStepWarning = false;
    isStepAlert = false;
    isDeadlineWarning = false;
    isDeadlineAlert = false;
    lastYearDocumentsReceivedDate = null;
    lastYearDocumentsReceivedDateByLanguage = null;
    isEFileStatusAlert = false;
    tooltipDisabled = signal(true);
    isHovered = signal(false);
    quietDaysFromNow = 0;
    thisYearCompletionDate = null;
    thisYearCompletionDateByLanguage = null;
    lastYearCompletedDate = null;
    lastYearCompletedDateByLanguage = null;
    tagNames: IT1Tags[] = [];
    documentReceivedLate = false;
    deadlineTip_Default = false;
    deadlineTip_Warning = false;
    deadlineTip_Alert = false;
    deadlineTip_Critical = false;
    deadlineDaysFromNow_Absolute = null;
    isTodayDeadlineDate = false;
    public isiFirmDashboard: boolean;
    public showLastYearReceivedDateColumn: boolean;
    public dragCard: boolean;
    public divId: string;
    private _divIdSet = false;

    public canEditAssignee: boolean;
    showPartnerManagerTooltips = false;
    currentLanguage = "en-CA";

    private readonly cardT1ColumnName = viewChild<ElementRef<HTMLElement>>("cardT1ColumnName");

    protected readonly complexityText = viewChild<ElementRef<HTMLElement>>("complexityText");
    protected showComplexityTooltip = signal<boolean>(false);

    constructor() {
        this.cardDisplayService.calcCardFieldLabelsEvent$.pipe(takeUntilDestroyed()).subscribe((data) => {
            if (this._cardData && data.cardId === this._cardData.cardId) {
                this.calcAllFieldLabels();
            }
        });

        this.languageService.langChangeEvent$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.calcAllFieldLabels(true);
        });

        this.cardViewService.refreshCardsViewLabelsEvent$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.calcAllFieldLabels();
            this.setTagNames();
            this.currentLanguage = this.languageService.getLang();
        });

        this.helpersService.favouriteFiltersPendingChangesEvent$.pipe(takeUntilDestroyed()).subscribe((hasPendingChanges) => {
            this.hasPendingFilterChanges = hasPendingChanges;
        });

        effect(() => {
            const cardT1ColumnName = this.cardT1ColumnName();
            if (!cardT1ColumnName) return;
            this.tooltipDisabled.set(cardT1ColumnName?.nativeElement.scrollWidth <= cardT1ColumnName?.nativeElement.clientWidth);
        });

        effect((onCleanup) => {
            const complexityText = this.complexityText();
            if (!complexityText) return;

            const el = complexityText.nativeElement;

            const observer = new ResizeObserver(() => {
                this.showComplexityTooltip.set(el.scrollWidth > el.clientWidth);
            });

            observer.observe(el);

            onCleanup(() => observer.disconnect());
        });

        effect(() => {
            this.canEditAssignee = this.iFirmContactInformationService.hasAccess(ContactsAccessType.ContactAssignees);
        });
    }

    ngOnInit() {
        this.currentLanguage = this.languageService.getLang();
    }

    openCard(e: MouseEvent) {
        e.preventDefault();

        if (this.hasPendingFilterChanges) {
            this.bsModalRef = this.modalService.show(PendingChangesPopupComponent, {
                initialState: {
                    onDiscard: () => {
                        this.helpersService.favouriteFiltersPendingChangesEmitter.next(false);
                        this.helpersService.revertToFavoriteFilterEmitter.next();
                        this.modalCoordinationService.closeAllModals();
                        this.navigateToCard();
                    },
                    onKeepEditing: () => {
                        // Just close modal
                    },
                },
                class: "custom-modal-rename-dashboard",
            });
            // Pass the modal ref to the component after it's created
            if (this.bsModalRef.content) {
                this.bsModalRef.content.bsModalRef = this.bsModalRef;
            }
        } else {
            this.navigateToCard();
            this.modalCoordinationService.closeAllModals();
        }
    }

    private navigateToCard(): void {
        if (this.hideArrows) {
            const matchedField = this.matchedField();
            const eventData = matchedField ? { matchedField } : {};
            this.analyticsService.track("OnePageApp : Opening a card after a search", eventData);
        }
        this.router.navigate(["/" + this.router.url, this._cardData.cardId]);
    }

    calcAllFieldLabels(skipCache = false) {
        const result = this.cardDisplayService.calcCardFieldLabels(this._cardData, this.statusIds, skipCache);
        this.complexity = this.cardDisplayService.calculateCategoryForCard(this._cardData)?.Name as TranslateObject;
        this.deadlineDateFromNow = result.deadlineDateFromNow;
        this.deadlineDaysFromNow = result.deadlineDaysFromNow;
        this.deadlineDate = result.deadlineDate;
        this.clientFileCount = result.clientFileCount;
        this.daysSinceInStep = result.daysSinceInStep;
        this.daysSinceReady = result.daysSinceReady;
        this.isStepWarning = result.isStepWarning;
        this.isStepAlert = result.isStepAlert;
        this.isDeadlineWarning = result.isDeadlineWarning;
        this.isDeadlineAlert = result.isDeadlineAlert;
        this.quietDaysFromNow = result.quietDaysFromNow;
        this.lastYearDocumentsReceivedDate = result.lastYearDocumentsReceivedDate;
        this.lastYearDocumentsReceivedDateByLanguage = result.lastYearDocumentsReceivedDateByLanguage;
        this.documentReceivedLate = result.documentReceivedLate;
        this.isEFileStatusAlert = result.isEFileStatusAlert;
        this.thisYearCompletionDate = result.thisYearCompletionDate;
        this.lastYearCompletedDate = result.lastYearCompletedDate;
        this.thisYearCompletionDateByLanguage = result.thisYearCompletionDateByLanguage;
        this.lastYearCompletedDateByLanguage = result.lastYearCompletedDateByLanguage;
        this.setDeadlineTooltip();
        this.deadlineDaysFromNow_Absolute = Math.abs(this.deadlineDaysFromNow);
        this.isTodayDeadlineDate = this.isDeadlineDateToday(this._cardData, this.deadlineDaysFromNow);
        this.isiFirmDashboard = isiFirmTaxDashboard(this.dashboardsService.currentDashboard());
        this.showLastYearReceivedDateColumn =
            !this.isiFirmDashboard || (this.isiFirmDashboard && this.dashboardsService.currentDashboard().TaxYear >= 2025);
        this.dragCard = this.noDragCard(this._cardData);
    }

    setTagNames() {
        const tagNames = [];
        if (!this._cardData.tags) {
            return false;
        }
        this._cardData.tags.forEach((cardTagName) => {
            const tag = this.helpersService.getT1Tags().find((tag: IT1Tags) => {
                return tag.Id === cardTagName;
            });
            if (tag && tag.Name && tag.Name[this.languageService.getLang()]) {
                tagNames.push(tag.Name[this.languageService.getLang()]);
            }
        });
        this.tagNames = tagNames;
        if (tagNames.length === 0) {
            this.translateService
                .get("Tooltips.NoTags")
                .pipe(take(1))
                .subscribe((string) => {
                    this.tagNames = [string];
                });
        }
    }

    setDeadlineTooltip(): boolean {
        this.deadlineTip_Default = false;
        this.deadlineTip_Warning = false;
        this.deadlineTip_Alert = false;
        this.deadlineTip_Critical = false;
        if (this.dashboardsService.currentDashboard().DaysBeforeDeadlineWarning != null) {
            if (this.deadlineDate != null) {
                if (this.deadlineDaysFromNow) {
                    if (
                        this.deadlineDaysFromNow < 0 &&
                        Math.abs(this.deadlineDaysFromNow) > this.dashboardsService.currentDashboard().DaysBeforeDeadlineWarning
                    ) {
                        this.deadlineTip_Default = true;
                        return true;
                    } else if (
                        this.deadlineDaysFromNow < 0 &&
                        this.dashboardsService.currentDashboard().DaysBeforeDeadlineWarning >= Math.abs(this.deadlineDaysFromNow) &&
                        Math.abs(this.deadlineDaysFromNow) > this.dashboardsService.currentDashboard().DaysBeforeDeadlineAlert
                    ) {
                        this.deadlineTip_Warning = true;
                        return true;
                    } else if (
                        this.deadlineDaysFromNow < 0 &&
                        this.dashboardsService.currentDashboard().DaysBeforeDeadlineAlert >= Math.abs(this.deadlineDaysFromNow)
                    ) {
                        this.deadlineTip_Alert = true;
                        return true;
                    } else if (this.deadlineDaysFromNow > 0) {
                        this.deadlineTip_Critical = true;
                        return true;
                    }
                } else if (this.deadlineDaysFromNow == 0 && this.deadlineDate != null) {
                    this.deadlineTip_Alert = true;
                    return true;
                }
            } else {
                this.deadlineTip_Default = true;
                return true;
            }
        } else {
            this.deadlineTip_Default = true;
            return true;
        }
    }

    isDeadlineDateToday(cardData: IBaseT1Card, dealineDays) {
        if (dealineDays < 0) {
            return true;
        } else {
            if (cardData.dateNeeded) {
                return format(fromUnixTime(cardData.dateNeeded), "MMMM d, yyyy") === format(Date.now(), "MMMM d, yyyy");
            } else if (cardData.deadLine) {
                return format(fromUnixTime(cardData.deadLine), "MMMM d, yyyy") === format(Date.now(), "MMMM d, yyyy");
            } else {
                return false;
            }
        }
    }

    showTooltipOnPartnerManager($e: MouseEvent, cardData: IBaseT1Card): void {
        this.isHovered.set(true);
        if (!this.showPartnerManagerTooltips) {
            const roleId = this.helpersService.mappingOfStatusesService.Steps.flatMap((step) => step.Statuses).find(
                (mapping) => mapping.TaxReturnStatusId === cardData.return.statusId,
            )?.RoleId;
            this.showPartnerManagerTooltips =
                this.dashboardsService.currentDashboard().ApplicationType === ApplicationType.iFirmTaxT1 &&
                this.cardViewService.currentViewDisplayMode === "details" &&
                this.canEditAssignee === false &&
                (roleId === "1" || roleId === "2");
        }
    }

    onMouseLeave(): void {
        this.isHovered.set(false);
    }

    generateDivId(cardId: string): string {
        cardId = cardId.replaceAll(/[{}]/g, "");
        return "div_" + cardId;
    }

    noDragCard(cardData: IBaseT1Card): boolean {
        const roleId = this.helpersService.mappingOfStatusesService.Steps.flatMap((step) => step.Statuses).find(
            (mapping) => mapping.TaxReturnStatusId === cardData.return.statusId,
        )?.RoleId;

        if (this.dashboardsService.currentDashboard()) {
            return (
                this.dashboardsService.currentDashboard().ApplicationType === ApplicationType.iFirmTaxT1 &&
                this.cardViewService.currentViewDisplayMode === "details" &&
                this.canEditAssignee === false &&
                (roleId === "1" || roleId === "2")
            );
        } else {
            return false;
        }
    }

    protected tooltipText(): string {
        let tooltip = this.translateService.instant("Tooltips.Deadline");
        if (this.deadlineTip_Warning && !this.deadlineTip_Critical && !this.deadlineTip_Alert) {
            tooltip =
                this.deadlineDaysFromNow_Absolute === 0 || this.deadlineDaysFromNow_Absolute === 1
                    ? this.translateService.instant("Tooltips.DeadlineWarning1") +
                      " " +
                      this.deadlineDaysFromNow_Absolute +
                      this.translateService.instant("Dashboard.AverageDay") +
                      this.translateService.instant("Tooltips.DeadlineWarning2")
                    : this.translateService.instant("Tooltips.DeadlineWarning1") +
                      " " +
                      this.deadlineDaysFromNow_Absolute +
                      this.translateService.instant("Dashboard.AverageDays") +
                      this.translateService.instant("Tooltips.DeadlineWarning2");
        }
        if (this.deadlineTip_Alert && !this.deadlineTip_Critical && !this.deadlineTip_Warning && this.isTodayDeadlineDate) {
            tooltip =
                this.deadlineDaysFromNow_Absolute === 0 || this.deadlineDaysFromNow_Absolute === 1
                    ? this.translateService.instant("Tooltips.DeadlineAlert1") +
                      " " +
                      this.deadlineDaysFromNow_Absolute +
                      this.translateService.instant("Dashboard.AverageDay") +
                      " " +
                      this.translateService.instant("Tooltips.TimeDuration2") +
                      this.translateService.instant("Tooltips.DeadlineAlert2")
                    : this.translateService.instant("Tooltips.DeadlineAlert1") +
                      " " +
                      this.deadlineDaysFromNow_Absolute +
                      this.translateService.instant("Dashboard.AverageDays") +
                      " " +
                      this.translateService.instant("Tooltips.TimeDuration2") +
                      this.translateService.instant("Tooltips.DeadlineAlert2");
        }
        if (this.deadlineTip_Alert && !this.deadlineTip_Critical && !this.deadlineTip_Warning && !this.isTodayDeadlineDate) {
            tooltip =
                this.deadlineDaysFromNow_Absolute === 0 || this.deadlineDaysFromNow_Absolute === 1
                    ? this.translateService.instant("Tooltips.DeadlineAlert1") +
                      " " +
                      this.deadlineDaysFromNow_Absolute +
                      this.translateService.instant("Dashboard.AverageDay") +
                      " " +
                      this.translateService.instant("Tooltips.TimeDuration1") +
                      this.translateService.instant("Tooltips.DeadlineAlert2")
                    : this.translateService.instant("Tooltips.DeadlineAlert1") +
                      " " +
                      this.deadlineDaysFromNow_Absolute +
                      this.translateService.instant("Dashboard.AverageDays") +
                      " " +
                      this.translateService.instant("Tooltips.TimeDuration1") +
                      this.translateService.instant("Tooltips.DeadlineAlert2");
        }
        if (this.deadlineTip_Critical && !this.deadlineTip_Warning && !this.deadlineTip_Alert) {
            tooltip =
                this.deadlineDaysFromNow_Absolute === 0 || this.deadlineDaysFromNow_Absolute === 1
                    ? this.translateService.instant("Tooltips.DeadlineCritical1") +
                      " " +
                      this.deadlineDaysFromNow_Absolute +
                      this.translateService.instant("Dashboard.AverageDay") +
                      this.translateService.instant("Tooltips.DeadlineCritical2")
                    : this.translateService.instant("Tooltips.DeadlineCritical1") +
                      " " +
                      this.deadlineDaysFromNow_Absolute +
                      this.translateService.instant("Dashboard.AverageDays") +
                      this.translateService.instant("Tooltips.DeadlineCritical2");
        }
        return tooltip;
    }
}
